import styles from './Footer.scss';

import cx from 'classnames';
import React, { CSSProperties, FC, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { Footer as FooterTypes } from 'src/api/contentful/contentTypes.cloverdotcom';
import { GridContainer } from 'src/components/GridContainer/GridContainer';
import { redesignV2RoutePath } from 'src/constants/redesignV2';
import { useDesktopOrLarge, useMobile, useTablet } from 'src/hooks/UseResponsive';
import { FlatEntry } from 'src/services/cms/flattenContentful';

import { FooterPrimaryNav } from './FooterPrimaryNav/FooterPrimaryNav';
import { FooterSecondaryNavDesktop } from './FooterSecondaryNavDesktop/FooterSecondaryNavDesktop';
import { FooterSecondaryNavMobile } from './FooterSecondaryNavMobile/FooterSecondaryNavMobile';
import { FooterSecondaryNavTablet } from './FooterSecondaryNavTablet/FooterSecondaryNavTablet';
import { useContentfulFooter } from './UseContentfulFooter';

export const Footer: FC = () => {
  const {
    primaryNavItems,
    primaryBackgroundColor,
    primaryBackgroundImage,
    primaryBackgroundSmallImage,
    secondaryNavItems: defaultSecondaryNavItems,
    socialLinks: defaultSocialLinks,
    legalLinks,
    ...data
  } = useContentfulFooter() || {};

  const { pathname } = useLocation();
  const isCompact = pathname === redesignV2RoutePath.CART;
  const secondaryNavItems = isCompact ? undefined : defaultSecondaryNavItems;
  const socialLinks = isCompact ? undefined : defaultSocialLinks;

  const isMobile = useMobile();
  const isTablet = useTablet();
  const isDesktopOrLarge = useDesktopOrLarge();
  const [primaryItemsHeight, setPrimaryItemsHeight] = useState(0);
  const [secondaryItemsHeight, setSecondaryItemsHeight] = useState(0);

  useEffect(() => {
    setPrimaryItemsHeight(0);
    setSecondaryItemsHeight(0);
  }, [isCompact]);

  return (
    <footer data-test="footer" className={cx(styles.footer, isCompact && 'is-compact')} style={getStyle(data, primaryItemsHeight, secondaryItemsHeight)}>
      <GridContainer gutter>
        {!isCompact && (
          <FooterPrimaryNav
            items={primaryNavItems}
            socialLinks={socialLinks}
            backgroundColor={primaryBackgroundColor}
            backgroundImage={primaryBackgroundImage}
            backgroundSmallImage={primaryBackgroundSmallImage}
            onItemsResizeUpdate={({ height }) => setPrimaryItemsHeight(height)}
          />
        )}
        {isMobile && (
          <FooterSecondaryNavMobile
            items={secondaryNavItems}
            socialLinks={socialLinks}
            legalLinks={legalLinks}
          />
        )}
        {isTablet && (
          <FooterSecondaryNavTablet
            items={secondaryNavItems}
            socialLinks={socialLinks}
            legalLinks={legalLinks}
            isCompact={isCompact}
          />
        )}
        {isDesktopOrLarge && (
          <FooterSecondaryNavDesktop
            items={secondaryNavItems}
            legalLinks={legalLinks}
            isCompact={isCompact}
            onItemsResizeUpdate={isCompact ? undefined : ({ height }) => setSecondaryItemsHeight(height)}
          />
        )}
      </GridContainer>
    </footer>
  );
};

Footer.displayName = 'Footer';

const getStyle = (
  {
    backgroundColor,
    primaryFontColor,
    primaryFontSize,
    primaryMobileFontSize,
    secondaryFontColor,
    secondaryFontSize,
    legalFontColor,
    legalFontSize,
    localePickerFontColor,
    localePickerFontSize,
  }: FlatEntry<FooterTypes>,
  primaryItemsHeight: number,
  secondaryItemsHeight: number
): CSSProperties => ({
    backgroundColor,
    '--primary-items-height': primaryItemsHeight,
    '--primary-font-color': primaryFontColor,
    '--primary-font-size': primaryFontSize,
    '--primary-mobile-font-size': primaryMobileFontSize,
    '--secondary-items-height': secondaryItemsHeight,
    '--secondary-font-color': secondaryFontColor,
    '--secondary-font-size': secondaryFontSize,
    '--legal-font-color': legalFontColor,
    '--legal-font-size': legalFontSize,
    '--locale-picker-font-color': localePickerFontColor,
    '--locale-picker-font-size': localePickerFontSize,
  } as CSSProperties
);
